import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";

/**
 * Servicio para gestión de incidencias
 */
class IncidenciasService {
  /**
   * Listar incidencias con filtros opcionales
   * @param {Object} params - Parámetros de filtrado
   * @param {string} params.estado - Estados CSV: 1 (abierta), 2 (en proceso), 3 (cerrada). Ej: '1,2'
   * @param {string} params.origen - Origen de la incidencia (sales, purchases, sticker, inventario, nave2)
   * @param {number} params.ejercicio - Año fiscal (2000-2100)
   * @param {string} params.tipoNote - Tipo de incidencia
   * @returns {Promise<Array>} Lista de incidencias en camelCase
   */
  static async list(params = {}) {
    try {
      const response = await axios.get(
        HostService.getHost() + "incidencias",
        { ...AuthService.setToken(), params }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error al listar incidencias:", error.message);
      throw error;
    }
  }

  /**
   * Contar incidencias que cumplen los filtros indicados
   * @param {Object} params - Filtros opcionales
   * @param {string} params.estado - Estados CSV: 1 (abierta), 2 (en proceso), 3 (cerrada)
   * @param {string} params.origen - Origen de la incidencia
   * @param {number} params.ejercicio - Año fiscal
   * @returns {Promise<number>} Número de incidencias
   */
  static async count(params = {}) {
    try {
      const response = await axios.get(
        HostService.getHost() + "incidencias/count",
        { ...AuthService.setToken(), params }
      );
      return response.data.data.count;
    } catch (error) {
      console.error("Error al contar incidencias:", error.message);
      throw error;
    }
  }

  /**
   * Obtener incidencia por número de documento
   * @param {string} serie - Serie del documento (puede ser null)
   * @param {string} albaran - Número de albarán
   * @param {string} origen - Origen de la incidencia
   * @returns {Promise<Object>} Incidencia encontrada
   */
  static async getByDocumento(serie, albaran, origen) {
    try {
      const response = await axios.get(
        HostService.getHost() +
          "note/" +
          (serie === null ? "undefined" : serie) +
          "/" +
          albaran +
          "/" +
          origen,
        AuthService.setToken()
      );
      return response.data;
    } catch (error) {
      console.error("Error al obtener incidencia por documento:", error.message);
      throw error;
    }
  }

  /**
   * Obtener incidencia por referencia
   * @param {string} referencia - Referencia de la incidencia
   * @param {string} origen - Origen de la incidencia
   * @param {number} estado - Estado de la incidencia (opcional)
   * @returns {Promise<Object>} Incidencia encontrada
   */
  static async getByReferencia(referencia, origen, estado) {
    try {
      let url = `${HostService.getHost()}note/${referencia}/${origen}`;

      if (estado !== undefined && estado !== null) {
        const queryParams = new URLSearchParams({ estado });
        url += `?${queryParams.toString()}`;
      }

      const response = await axios.get(url, AuthService.setToken());
      return response.data;
    } catch (error) {
      console.error("Error al obtener incidencia por referencia:", error.message);
      throw error;
    }
  }

  /**
   * Crear nueva incidencia
   * @param {Object} data - Datos de la incidencia
   * @param {string} data.serie - Serie del documento
   * @param {string} data.albaran - Número de albarán
   * @param {number} data.idEmpleado - ID del empleado
   * @param {string} data.nombreEmpleado - Nombre del empleado
   * @param {string} data.note - Texto de la incidencia
   * @param {string} data.origen - Origen de la incidencia
   * @param {number} data.estado - Estado de la incidencia
   * @param {string} data.codigoArticulo - Código del artículo (opcional)
   * @param {string} data.tipoNote - Tipo de incidencia
   * @returns {Promise<Object>} Incidencia creada
   */
  static async create(data) {
    try {
      const response = await axios.post(
        HostService.getHost() + "notes",
        {
          serie: data.serie?.toString(),
          albaran: data.albaran?.toString(),
          id_empleado: data.idEmpleado,
          nombre_empleado: data.nombreEmpleado,
          note: data.note,
          origen: data.origen,
          estado: data.estado,
          codigo_articulo: data.codigoArticulo,
          tipo_note: data.tipoNote,
        },
        AuthService.setToken()
      );
      return response.data;
    } catch (error) {
      console.error("Error al crear incidencia:", error.message);
      throw error;
    }
  }

  /**
   * Actualizar incidencia existente
   * @param {number} idNote - ID de la incidencia
   * @param {Object} data - Datos a actualizar
   * @param {string} data.note - Texto de la incidencia
   * @param {number} data.estado - Estado de la incidencia
   * @returns {Promise<Object>} Incidencia actualizada
   */
  static async update(idNote, data) {
    try {
      const response = await axios.put(
        HostService.getHost() + "notes/" + idNote,
        {
          note: data.note,
          estado: data.estado,
        },
        AuthService.setToken()
      );
      return response.data;
    } catch (error) {
      console.error("Error al actualizar incidencia:", error.message);
      throw error;
    }
  }

  /**
   * Crear comentario en una incidencia (con soporte para imágenes)
   * @param {Object} data - Datos del comentario
   * @param {number} data.idNote - ID de la incidencia
   * @param {string} data.comentario - Texto del comentario
   * @param {string} data.nombreEmpleado - Nombre del empleado
   * @param {Array<File>} data.imagenes - Archivos de imagen (opcional)
   * @param {boolean} data.enviarEmail - Enviar el comentario por correo al cliente de la factura (opcional)
   * @returns {Promise<Object>} Comentario creado
   */
  static async createComment(data) {
    try {
      const formData = new FormData();
      formData.append("id_note", data.idNote);
      formData.append("comentario", data.comentario);
      formData.append("nombre_empleado", data.nombreEmpleado);
      if (data.enviarEmail) {
        formData.append("enviar_email", "true");
      }

      if (data.imagenes && data.imagenes.length > 0) {
        data.imagenes.forEach((file) => {
          formData.append("imagenes[]", file);
        });
      }

      const config = {
        headers: {
          ...AuthService.setToken().headers,
        },
      };

      const response = await axios.post(
        HostService.getHost() + "notes/comentario",
        formData,
        config
      );
      return response.data;
    } catch (error) {
      console.error("Error al crear comentario:", error.message);
      throw error;
    }
  }

  /**
   * Obtener información de factura
   * @param {string} serie - Serie de la factura
   * @param {string} albaran - Número de albarán
   * @param {number} ejercicio - Ejercicio fiscal
   * @returns {Promise<Object>} Información de la factura
   */
  static async getInfoInvoice(serie, albaran, ejercicio) {
    try {
      const response = await axios.get(
        HostService.getHost() + "invoice/" + serie + "/" + albaran,
        AuthService.setToken()
      );
      return response.data;
    } catch (error) {
      console.error("Error al obtener información de factura:", error.message);
      throw error;
    }
  }
}

export default IncidenciasService;
